import { FormGroup } from '@angular/forms';
import { BookingSummaryDto } from '@opb-workspace/shared';

/** Número de reserva preferido para UI y estado (carrier si existe en el listado). */
export function bookingNumberDisplayFromSummary(b: BookingSummaryDto): string {    const carrierBooking = (b.carrierBookingNumber ?? '').trim();
    if (carrierBooking.length > 0) {
        return carrierBooking;
    }
    return (b.bookingNumber ?? '').trim();
}

export function formatBookingSummaryLabel(b: BookingSummaryDto): string {
    const num = bookingNumberDisplayFromSummary(b);
    const cust = b.customerName ?? '';
    const cnt =
        b.containerCount != null && !Number.isNaN(Number(b.containerCount))
            ? b.containerCount
            : 0;
    return `${num} (${cust}) — ${cnt} contenedores`;
}

/**
 * Unifica el valor que devuelve mat-option / displayWith (objeto parcial, id numérico,
 * o claves PascalCase desde JSON) con la fila canónica de `availableBookings`.
 */
export function coerceBookingSummaryLike(
    raw: unknown,
    available: BookingSummaryDto[],
): BookingSummaryDto | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return available.find((x) => x.bookingId === raw) ?? null;
    }
    if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
        const id = Number(raw.trim());
        return available.find((x) => x.bookingId === id) ?? null;
    }
    if (typeof raw !== 'object') {
        return null;
    }
    const o = raw as Record<string, unknown>;
    const idRaw = o['bookingId'] ?? o['BookingId'];
    let id: number | undefined;
    if (typeof idRaw === 'number' && Number.isFinite(idRaw)) {
        id = idRaw;
    } else if (typeof idRaw === 'string' && /^\d+$/.test(idRaw.trim())) {
        id = Number(idRaw.trim());
    }
    if (id != null) {
        const full = available.find((x) => x.bookingId === id);
        if (full) {
            return full;
        }
    }

    const bn = o['bookingNumber'] ?? o['BookingNumber'];
    const cbn = o['carrierBookingNumber'] ?? o['CarrierBookingNumber'];
    const cn = o['customerName'] ?? o['CustomerName'];
    const sn = o['shipperName'] ?? o['ShipperName'];
    const cc = o['containerCount'] ?? o['ContainerCount'];
    const pkg = o['packageCount'] ?? o['PackageCount'];
    const hid =
        o['hasActiveShippingInstruction'] ??
        o['HasActiveShippingInstruction'];
    const cid = o['customerId'] ?? o['CustomerId'];
    const asStr = (x: unknown): string => {
        if (x == null) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'number' || typeof x === 'boolean') return String(x);
        return '';
    };

    return {
        bookingId: id ?? Number(idRaw ?? 0),
        bookingNumber: asStr(bn),
        carrierBookingNumber: asStr(cbn) || undefined,
        customerId: cid == null || cid === '' ? 0 : Number(cid),
        customerName: asStr(cn),
        shipperName: asStr(sn) || undefined,
        containerCount: cc == null || cc === '' ? 0 : Number(cc),
        packageCount: pkg == null || pkg === '' ? 0 : Number(pkg),
        hasActiveShippingInstruction: Boolean(hid),
    };
}

/** Texto de búsqueda cuando el control puede ser string (tecleo) u objeto (selección Material). */
export function searchTextFromBookingInputValue(
    raw: unknown,
    available: BookingSummaryDto[],
): string {
    if (typeof raw === 'string') {
        return raw;
    }
    const n = coerceBookingSummaryLike(raw, available);
    return n ? formatBookingSummaryLabel(n) : '';
}

/** Unifica guión largo (UI) y ASCII para comparar etiqueta vs listado. */
function normalizeBookingLabelForCompare(s: string): string {
    return s
        .replace(/\u2014/g, '-')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

/**
 * Si el control queda como string (usuario escribe o restauración antigua), mostrarlo tal cual.
 * Si es objeto/DTO, formatear. Evita vaciar el input al guardar la etiqueta como string en el model.
 */
export function createDisplayBookingSummaryFn(
    getAvailable: () => BookingSummaryDto[],
): (b: BookingSummaryDto | string | null) => string {
    return (b) => {
        if (b === null || b === undefined) return '';
        if (typeof b === 'string') return b;
        const n = coerceBookingSummaryLike(b, getAvailable());
        return n ? formatBookingSummaryLabel(n) : '';
    };
}

export function filterBookingSummaries(
    list: BookingSummaryDto[],
    queryRaw: string,
): BookingSummaryDto[] {
    const q = String(queryRaw ?? '')
        .trim()
        .toLowerCase();
    if (!q) return [...list];
    return list.filter(
        (b) =>
            (b.carrierBookingNumber ?? '').toLowerCase().includes(q) ||
            (b.bookingNumber ?? '').toLowerCase().includes(q) ||
            (b.customerName ?? '').toLowerCase().includes(q) ||
            String(b.bookingId).includes(q),
    );
}

/** Si el usuario editó el texto y ya no coincide con el booking seleccionado, limpia el id. */
export function clearStaleBookingSelection(
    form: FormGroup,
    list: BookingSummaryDto[],
    inputRaw: unknown,
): void {
    const raw =
        typeof inputRaw === 'string'
            ? inputRaw.trim()
            : (() => {
                  const n = coerceBookingSummaryLike(inputRaw, list);
                  return n ? formatBookingSummaryLabel(n) : '';
              })();
    const id = form.get('selectedBookingId')?.value;
    if (id == null) return;
    const sel = list.find((b) => b.bookingId === id);
    const expected = sel ? normalizeBookingLabelForCompare(formatBookingSummaryLabel(sel)) : '';
    const actual = normalizeBookingLabelForCompare(raw);
    if (sel && expected !== actual) {
        form.patchValue({ selectedBookingId: null }, { emitEvent: false });
    }
}
